import { BRAND_NAME } from './components/ui/Brand';
import { enablePush,registerExistingPush,PUSH_HELP,disconnectPush } from './utils/pushNotifications';
import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { apiFetch, setGlobalToast } from './utils/api';
import { API_BASE } from './utils/apiBase';
import { ToastProvider, useToast } from './components/ui/Toast';
import { AppLayout } from './components/layout/AppLayout';
import { RestaurantPickerPage } from './pages/RestaurantPickerPage';
import { ManagerSetupPage } from './pages/ManagerSetupPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { keepBackendAwake } from './keepAlive';
import ProgressBar from './components/ui/ProgressBar';
import EmployeeSetupPage from './pages/EmployeeSetupPage';
import ErrorBoundary from './components/ErrorBoundary';
import { NetworkStatusBanner } from './components/ui/NetworkStatusBanner';
import { CommandPalette } from './components/ui/CommandPalette';
import { AlertTriangle, Bell, X } from 'lucide-react';
import { payrollLocation } from './utils/payrollLink';
import LegalGate from './components/legal/LegalGate';
const LegalPage = lazy(() => import('./pages/LegalPage'));
const LegalRecordsPage = lazy(() => import('./pages/LegalRecordsPage'));
const AdminInvitePage = lazy(() => import('./pages/AdminInvitePage'));
const SecurityPage = lazy(() => import('./pages/SecurityPage'));
const MfaRecoveryPage = lazy(() => import('./pages/MfaRecoveryPage'));

const ApprovalsPage = lazy(() => import('./pages/ApprovalsPage'));
const TimesheetsPage = lazy(() => import('./pages/TimesheetsPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const PeopleDirectoryPage = lazy(() => import('./pages/PeopleDirectoryPage'));

const DashboardPage    = lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const EmployeesPage    = lazy(() => import('./pages/EmployeesPage').then(m => ({ default: m.EmployeesPage })));
const SchedulePage     = lazy(() => import('./pages/SchedulePage').then(m => ({ default: m.SchedulePage })));
const ClockInOutPage   = lazy(() => import('./pages/ClockInOutPage').then(m => ({ default: m.ClockInOutPage })));
const PayrollPage      = lazy(() => import('./pages/PayrollPage'));
const ManagersPage     = lazy(() => import('./pages/ManagersPage').then(m => ({ default: m.ManagersPage })));
const VendorOrdersPage = lazy(() => import('./pages/VendorOrdersPage'));
const AdminToolsPage   = lazy(() => import('./pages/AdminToolsPage'));
const KioskLinksPage   = lazy(() => import('./pages/KioskLinksPage'));
const EmployeePortalPage = lazy(() => import('./pages/EmployeePortalPage'));
const KioskPublicPage  = lazy(() => import('./pages/KioskPublicPage').then(m => ({ default: m.KioskPublicPage })));
const LoginPage        = lazy(() => import('./pages/LoginPage').then(m => ({ default: m.LoginPage })));

const RestaurantsPage  = lazy(() => import('./pages/RestaurantsPage'));
const ReportsPage      = lazy(() => import('./pages/ReportsPage').then(m => ({ default: m.ReportsPage })));
const AdminOverviewPage = lazy(() => import('./pages/AdminOverviewPage'));
const FinancialPage    = lazy(() => import('./pages/finance/FinancialPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const InfluencerVisitsPage = lazy(() => import('./pages/InfluencerVisitsPage'));
const PnLReportPage         = lazy(() => import('./pages/PnLReportPage'));
const AuditsPage            = lazy(() => import('./pages/AuditsPage'));
const ChecklistReportPage   = lazy(() => import('./pages/ChecklistReportPage'));
const CashOutsPage          = lazy(() => import('./pages/CashOutsPage').then(m => ({ default: m.CashOutsPage })));

function PageLoader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#64748b', fontSize: '14px', fontFamily: 'Inter, sans-serif' }}>
      Loading...
    </div>
  );
}

function KioskErrorFallback() {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: 24,
      padding: 40,
      background: '#1e293b',
      color: '#f1f5f9',
      fontFamily: 'Inter, sans-serif',
      textAlign: 'center',
    }}>
      <AlertTriangle size={64} color="#f59e0b" aria-hidden="true" />
      <div style={{ fontSize: 28, fontWeight: 700 }}>Kiosk Error</div>
      <div style={{ fontSize: 17, color: '#94a3b8', maxWidth: 380, lineHeight: 1.5 }}>
        An unexpected error occurred. Reload the page to restore the kiosk.
      </div>
      <button
        onClick={() => window.location.reload()}
        style={{
          padding: '18px 48px',
          fontSize: 20,
          fontWeight: 700,
          background: '#f59e0b',
          color: '#000',
          border: 'none',
          borderRadius: 14,
          cursor: 'pointer',
          marginTop: 8,
        }}
      >
        Reload Kiosk
      </button>
    </div>
  );
}

function KioskLoader() {
  const { token } = useParams();
  const [restaurantId, setRestaurantId] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    localStorage.setItem('kioskToken', token);
    const apiBase = API_BASE;
    fetch(`${apiBase}/api/kiosk/resolve/${token}`)
      .then(r => r.json())
      .then(data => { setRestaurantId(data.ok ? data.restaurantId : token); setDone(true); })
      .catch(() => { setRestaurantId(token); setDone(true); });
  }, [token]);

  if (!done) return <PageLoader />;
  return <KioskPublicPage restaurantId={restaurantId} />;
}

export const PAGE_TO_PATH = {
  dashboard:     '/dashboard',
  employees:     '/employees',
  schedule:      '/schedule',
  timesheets:    '/timesheets',
  approvals:     '/approvals',
  settings:      '/settings',
  people:        '/people',
  clockin:       '/clockin',
  payroll:       '/payroll',
  vendor:        '/vendor',
  managers:      '/managers',
  restaurants:   '/restaurants',
  admintools:    '/admintools',
  kiosklinks:    '/kiosklinks',
  reports:       '/reports',
  profits:       '/profits',
  notifications: '/notifications',
  visits:        '/visits',
  monthlypnl:    '/monthlypnl',
  audits:        '/audits',
  cashouts:      '/cashouts',
};

const PATH_TO_PAGE = Object.fromEntries(Object.entries(PAGE_TO_PATH).map(([k, v]) => [v, k]));

const PAGE_TITLES = {
  dashboard:     'Dashboard',
  employees:     'People',
  schedule:      'Schedule',
  timesheets:    'Timesheets',
  approvals:     'Approvals',
  settings:      'Settings',
  people:        'People',
  clockin:       'Attendance',
  payroll:       'Payroll',
  vendor:        'Vendor orders',
  managers:      'Managers',
  restaurants:   'Locations',
  admintools:    'Settings & records',
  kiosklinks:    'Kiosk Links',
  reports:       'Reports',
  profits:       'Financials',
  notifications: 'Notifications',
  visits:        'Influencer Visits',
  monthlypnl:    'Location financials',
  audits:        'Checklists',
  cashouts:      'Cash-Outs',
};

// Pages a given role should NOT see in the command palette (admin sees all).
const PALETTE_EXCLUDE = {
  manager: new Set(['people', 'restaurants', 'managers', 'profits', 'monthlypnl', 'kiosklinks', 'admintools', 'visits']),
  general_manager: new Set(['restaurants']),
  admin: new Set(),
};
function getPalettePages(role) {
  const excluded = PALETTE_EXCLUDE[role] || PALETTE_EXCLUDE.manager;
  return Object.keys(PAGE_TITLES)
    .filter(id => !excluded.has(id))
    .map(id => ({ id, title: PAGE_TITLES[id] }));
}

function AppRoutes({ auth }) {
  keepBackendAwake();
  const { user, saveUser, logout, refreshSession, theme, toggleTheme } = auth;
  const toast = useToast();
  useEffect(() => { setGlobalToast(toast); }, [toast]);

  useEffect(() => {
    const expired = localStorage.getItem('scheduler_session_expired');
    if (expired === '1') {
      localStorage.removeItem('scheduler_session_expired');
      toast('Your session has expired. Please log in again.', 'error');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [restaurant, setRestaurant] = useState(null);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const restaurantHydratedRef = useRef(false);
  const [viewMode, setViewMode] = useState('manager');
  const prevPathRef = useRef(null);
  const [scheduleTab, setScheduleTab] = useState('grid');
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [notificationToOpen,setNotificationToOpen]=useState(null);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushBlocked, setPushBlocked] = useState(false);
  const [pushPending, setPushPending] = useState(false);
  const [pushMessage, setPushMessage] = useState(null);
  const [pushDismissed, setPushDismissed] = useState(() => localStorage.getItem('pushBannerDismissed') === '1');
  const [allRestaurants, setAllRestaurants] = useState([]);
  const [restaurantsLoading, setRestaurantsLoading] = useState(false);
  const [restaurantsLoaded, setRestaurantsLoaded] = useState(false);

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'general_manager')) return;
    setRestaurantsLoading(true);
    setRestaurantsLoaded(false);
    apiFetch('/api/restaurants').then(data => {
      if (data.ok) setAllRestaurants(data.restaurants.filter(r => r.active !== false));
      setRestaurantsLoading(false);
      setRestaurantsLoaded(true);
    });
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist selected restaurant to sessionStorage so admin context survives a page refresh.
  // Skip the initial mount (restaurant = null) so externally-seeded sessionStorage isn't wiped
  // before the restore effect below can read it.
  useEffect(() => {
    if (!restaurantHydratedRef.current) { restaurantHydratedRef.current = true; return; }
    if (restaurant) sessionStorage.setItem('scheduler_restaurant_id', restaurant.id);
    else sessionStorage.removeItem('scheduler_restaurant_id');
  }, [restaurant]);

  // After allRestaurants loads, restore the previously selected restaurant for admins.
  useEffect(() => {
    if (restaurant || !allRestaurants.length || (user?.role !== 'admin' && user?.role !== 'general_manager')) return;
    const savedId = sessionStorage.getItem('scheduler_restaurant_id');
    if (!savedId) return;
    const r = allRestaurants.find(x => x.id === savedId);
    if (r) setRestaurant(r);
  }, [allRestaurants]); // eslint-disable-line react-hooks/exhaustive-deps

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const page = PATH_TO_PAGE[location.pathname] || 'dashboard';
  const payrollLocationId = location.pathname === '/payroll' ? searchParams.get('restaurantId') : null;
  useEffect(() => {
    if (!user || !payrollLocationId || user.role === 'employee') return;
    if (['admin', 'general_manager'].includes(user.role) && !restaurantsLoaded) return;
    const target = payrollLocation(user, allRestaurants, payrollLocationId);
    if (!target) { toast('This payroll location is unavailable or you do not have access.', 'error'); navigate('/', {replace:true}); return; }
    setRestaurant(target);
    setViewMode('manager');
  }, [user?.id, payrollLocationId, restaurantsLoaded, allRestaurants]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (location.pathname !== '/schedule') setScheduleTab('grid');
  }, [location.pathname]);

  useEffect(() => {
    const title = PAGE_TITLES[page];
    document.title = title ? `${title} — ${BRAND_NAME}` : BRAND_NAME;
  }, [page]);

  useEffect(() => {
    async function checkVersion() {
      try {
        const res = await fetch('/version.json?t=' + Date.now());
        const data = await res.json();
        const currentVersion = localStorage.getItem('appVersion');
        if (currentVersion && currentVersion !== data.version) setUpdateAvailable(true);
        localStorage.setItem('appVersion', data.version);
      } catch (e) { console.warn('Version check failed:', e.message); }
    }
    checkVersion();
    const interval = setInterval(checkVersion, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(()=>{
    if(!user)return;
    const refresh=()=>{
      registerExistingPush().then(setPushSubscribed).catch(()=>setPushSubscribed(false));
      setPushBlocked('Notification' in window && Notification.permission==='denied');
    };
    refresh();window.addEventListener('push-registration-changed',refresh);
    return()=>window.removeEventListener('push-registration-changed',refresh);
  },[user]);

  useEffect(()=>{
    const id=searchParams.get('notification');
    if(!user||!id)return;
    if(['admin','general_manager'].includes(user.role)&&!allRestaurants.length)return;
    let active=true;
    apiFetch(`/api/notifications/${encodeURIComponent(id)}`).then(data=>{
      if(!active)return;
      if(!data.ok){toast(data.message,'error');navigate('/notifications',{replace:true});return;}
      const n=data.notification;
      apiFetch(`/api/notifications/${encodeURIComponent(id)}/read`,{method:'PATCH'});
      if(user.role==='employee'||viewMode==='employee'){
        setNotificationToOpen(n);navigate('/dashboard',{replace:true});return;
      }
      const r=allRestaurants.find(item=>item.id===n.restaurantId);
      if(['admin','general_manager'].includes(user.role)&&!r){toast('This location is no longer available.','error');navigate('/notifications',{replace:true});return;}
      if(r)setRestaurant(r);
      else if(n.restaurantId===user.restaurantId)setRestaurant({id:n.restaurantId,name:n.restaurantName});
      else {toast('Open your employee view to see this notification for another location.','error');navigate('/notifications',{replace:true});return;}
      const target=n.target||{};
      if(target.screen==='schedule')setScheduleTab(['timesheets','timeoff','openShifts','announcements'].includes(target.tab)?target.tab:'grid');
      const query=new URLSearchParams();
      if(target.date)query.set('date',target.date);
      if(target.weekStart)query.set('weekStart',target.weekStart);
      navigate((PAGE_TO_PATH[target.screen||n.route]||'/dashboard')+(query.size?'?'+query:''),{replace:true});
    });
    return()=>{active=false;};
  },[user?.id,searchParams,allRestaurants,viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState === 'visible' && user) {
        refreshSession();
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      refreshSession();
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleLogin(token, userData, rememberMe = true) {
    saveUser(token, userData, rememberMe);
  }

  function handleLogout() {
    setShowSignOutConfirm(true);
  }

  async function confirmLogout() {
    try{await disconnectPush();}catch{/* Browser unsubscribe below still prevents local delivery. */
      try{const reg=await navigator.serviceWorker?.getRegistration();const sub=await reg?.pushManager.getSubscription();await sub?.unsubscribe();}catch{}
    }
    setShowSignOutConfirm(false);
    try { await logout(); } catch (error) { toast(error.message, 'error'); return; }
    setNotificationToOpen(null);
    setRestaurant(null);
    navigate('/');
  }

  function handleSelectRestaurant(r) {
    setRestaurant(r);
    // Managers auto-select their location on reload. Keep the requested page
    // and its query parameters instead of replacing a saved link with Home.
    if (!PATH_TO_PAGE[location.pathname]) navigate('/dashboard');
  }

  function handleSwitchRestaurant() {
    setRestaurant(null);
    navigate('/');
  }

  function handleSelectRestaurantTab(r) {
    setRestaurant(r);
    if (!r || !restaurant) navigate('/dashboard');
    else if (payrollLocationId) navigate('/payroll');
  }

  function handleFinancialLocation(id) {
    const selected = allRestaurants.find(r => r.id === id);
    if (selected) handleSelectRestaurant(selected);
  }

  function handleOpenRestaurant(r, targetPage = 'dashboard', targetTab = null, personName = null) {
    if (targetPage === 'schedule' && targetTab) setScheduleTab(targetTab);
    setRestaurant(r);
    navigate((PAGE_TO_PATH[targetPage] || '/dashboard') + (personName ? `?search=${encodeURIComponent(personName)}` : ''));
  }

  function handleAdminNotifNavigate(restaurantId, route) {
    const r = allRestaurants.find(x => x.id === restaurantId);
    if (r) handleOpenRestaurant(r, route || 'dashboard');
  }

  async function handleSubscribeToPush(){
    if(pushPending)return;
    setPushPending(true);setPushMessage(null);
    try{await enablePush();setPushSubscribed(true);setPushBlocked(false);}
    catch(error){setPushMessage({type:'error',text:error.message||PUSH_HELP});}
    finally{setPushPending(false);}
  }

  function handleNavigate(id) {
    navigate(PAGE_TO_PATH[id] || '/dashboard');
  }

  const updateBanner = updateAvailable ? (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 99999,
      background: 'var(--brand)',
      color: 'var(--brand-contrast)', padding: '10px 16px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      fontSize: '13px', fontWeight: '600',
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
    }}>
      <span>A new version is available</span>
      <button
        onClick={() => {
          localStorage.removeItem('appVersion');
          window.location.href = window.location.origin + window.location.pathname + '?v=' + Date.now();
        }}
        style={{
          background: 'rgba(255,255,255,0.25)', border: '1px solid rgba(255,255,255,0.4)',
          borderRadius: '6px', color: 'var(--brand-contrast)', padding: '5px 12px',
          fontSize: '12px', fontWeight: '700', cursor: 'pointer',
        }}
      >
        Refresh Now
      </button>
    </div>
  ) : null;

  const pushBanner = restaurant && user && user.role !== 'employee' && !pushSubscribed && !pushDismissed ? (
    <details className="device-notice-disclosure" open={pushMessage ? true : undefined}>
      <summary><Bell size={16} aria-hidden="true" /> Device notifications{pushMessage?.type === 'error' ? ' · Needs attention' : ''}</summary>
    <aside className={`device-notice${pushMessage?.type === 'error' ? ' is-error' : ''}`} aria-label="Device notifications">
      <Bell size={18} aria-hidden="true" />
      <div className="device-notice-copy">
        {pushMessage ? <span role="status">{pushMessage.text}</span> : <>
          <strong>{pushBlocked ? 'Notifications are off on this device' : 'Get shift and request alerts on this device'}</strong>
          <details><summary>Setup help</summary><p>{PUSH_HELP}</p></details>
        </>}
      </div>
      <div className="device-notice-actions">
        {!pushBlocked && !pushMessage && <button className="btn btn-secondary btn-sm" onClick={handleSubscribeToPush} disabled={pushPending}>{pushPending ? 'Enabling…' : 'Enable'}</button>}
        <button className="icon-action" aria-label={pushMessage ? 'Dismiss notification message' : 'Dismiss notification setup'} title={pushMessage ? 'Dismiss message' : "Don't show again on this device"}
          onClick={() => { if (pushMessage) setPushMessage(null); else { localStorage.setItem('pushBannerDismissed', '1'); setPushDismissed(true); } }}><X size={18} aria-hidden="true" /></button>
      </div>
    </aside>
    </details>
  ) : null;

  // Setup / reset pages via query params (one-time email links)
  const setupToken = searchParams.get('setup');
  const setupEmail = searchParams.get('email');
  const empSetupToken = searchParams.get('empsetup');
  const empSetupRestaurant = searchParams.get('restaurant');
  const resetToken = searchParams.get('reset');

  if (setupToken && setupEmail) {
    return <>{updateBanner}<ManagerSetupPage token={setupToken} email={setupEmail} /></>;
  }
  if (empSetupToken && empSetupRestaurant) {
    return <>{updateBanner}<EmployeeSetupPage token={empSetupToken} restaurantId={empSetupRestaurant} /></>;
  }
  if (resetToken && !user) {
    return <>{updateBanner}<ResetPasswordPage token={resetToken} /></>;
  }

  if (!user) {
    const storedKioskToken = localStorage.getItem('kioskToken');
    if (storedKioskToken && !searchParams.get('notification') && location.pathname!=='/notifications' && !payrollLocationId) {
      window.location.replace(`/kiosk/${storedKioskToken}`);
      return null;
    }
    return <>{updateBanner}<LoginPage onLogin={handleLogin} /></>;
  }

  const signOutModal = showSignOutConfirm ? (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999, padding: '24px', fontFamily: 'Inter,system-ui,sans-serif' }}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '16px', padding: '32px 28px', maxWidth: '360px', width: '100%', textAlign: 'center', boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(248,113,113,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
        </div>
        <div style={{ fontSize: '17px', fontWeight: '800', color: 'var(--text)', marginBottom: '8px' }}>Sign out?</div>
        <div style={{ fontSize: '13px', color: 'var(--text-2)', marginBottom: '24px', lineHeight: 1.5 }}>You'll need to log in again to access the app.</div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => setShowSignOutConfirm(false)} style={{ flex: 1, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '10px', padding: '12px', color: 'var(--text-2)', fontSize: '14px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
          <button onClick={confirmLogout} style={{ flex: 1, background: 'linear-gradient(135deg,#ef4444,#dc2626)', border: 'none', borderRadius: '10px', padding: '12px', color: '#fff', fontSize: '14px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' }}>Sign Out</button>
        </div>
      </div>
    </div>
  ) : null;

  if (user.role === 'employee') {
    return <>{updateBanner}{signOutModal}<EmployeePortalPage user={user} showNotifications={location.pathname==='/notifications'&&!searchParams.get('notification')} notificationToOpen={notificationToOpen} onNotificationOpened={()=>setNotificationToOpen(null)} onLogout={handleLogout} /></>;
  }

  if ((user.role === 'manager' || user.role === 'admin' || user.role === 'general_manager') && viewMode === 'employee') {
    return (
      <>
        {updateBanner}
        {signOutModal}
        <EmployeePortalPage
          user={user}
          onLogout={handleLogout}
          onSwitchToManager={() => {
            setViewMode('manager');
            if (prevPathRef.current) navigate(prevPathRef.current);
          }}
          showNotifications={location.pathname==='/notifications'&&!searchParams.get('notification')} notificationToOpen={notificationToOpen} onNotificationOpened={()=>setNotificationToOpen(null)}
          restaurantIdOverride={restaurant?.id}
        />
      </>
    );
  }

  const isPrivileged = user.role === 'admin' || user.role === 'general_manager';

  if (payrollLocationId && restaurant?.id !== payrollLocationId) return <PageLoader />;

  if (!restaurant) {
    if (!isPrivileged) {
      return (
        <>
          {updateBanner}
          <RestaurantPickerPage
            user={user}
            onSelect={handleSelectRestaurant}
            onLogout={handleLogout}
            theme={theme}
            onToggleTheme={toggleTheme}
          />
        </>
      );
    }
    if (restaurantsLoading) {
      return <PageLoader />;
    }
  }

  // Pages that work without a restaurant context (admin + GM share most, some admin-only)
  const ADMIN_GLOBAL_PAGES = ['managers', 'restaurants', 'admintools', 'kiosklinks', 'profits', 'visits', 'audits', 'notifications', 'settings', 'people'];
  const GM_GLOBAL_PAGES    = ['managers', 'kiosklinks', 'visits', 'audits', 'admintools', 'notifications', 'settings', 'people'];

  function renderPage() {
    const globalPages = user.role === 'admin' ? ADMIN_GLOBAL_PAGES : GM_GLOBAL_PAGES;
    if (!restaurant && isPrivileged && !globalPages.includes(page)) {
      return (
        <AdminOverviewPage
          user={user}
          onOpenRestaurant={handleOpenRestaurant}
          theme={theme}
        />
      );
    }
    switch (page) {
      case 'dashboard':
        return <DashboardPage user={user} restaurant={restaurant} onNavigate={handleNavigate} />;
      case 'employees':
        return <EmployeesPage key={restaurant.id} restaurant={restaurant} />;
      case 'people':
        return isPrivileged ? <PeopleDirectoryPage user={user} restaurants={allRestaurants} onOpenRestaurant={handleOpenRestaurant} /> : <EmployeesPage restaurant={restaurant} />;
      case 'settings':
        return <SettingsPage user={user} restaurant={restaurant} />;
      case 'approvals':
        return <ApprovalsPage key={restaurant.id} restaurant={restaurant} />;
      case 'timesheets':
        return <TimesheetsPage key={restaurant.id} restaurant={restaurant} />;
      case 'schedule':
        return <SchedulePage key={restaurant.id} restaurant={restaurant} scheduleTab={scheduleTab} onScheduleTabChange={setScheduleTab} />;
      case 'clockin':
        return <ClockInOutPage key={restaurant.id} restaurant={restaurant} user={user} />;
      case 'payroll':
        return <PayrollPage key={restaurant.id} user={user} restaurantId={restaurant.id} />;
      case 'managers':
        return <ManagersPage user={user} />;
      case 'restaurants':
        return <RestaurantsPage user={user} />;
      case 'vendor':
        return <VendorOrdersPage key={restaurant.id} user={user} restaurantId={restaurant.id} restaurantName={restaurant.name} initialSettingsOpen={searchParams.get('settings') === '1'} />;
      case 'admintools':
        return <AdminToolsPage user={user} restaurant={restaurant} />;
      case 'kiosklinks':
        return <KioskLinksPage user={user} />;
      case 'reports':
        return <ReportsPage restaurant={restaurant} />;
      case 'profits':
        return <FinancialPage restaurantId={restaurant?.id} onLocationChange={handleFinancialLocation} />;
      case 'notifications':
        return <NotificationsPage user={user} restaurantId={restaurant?.id} userRole={user.role} onAdminNotifNavigate={handleAdminNotifNavigate} />;
      case 'visits':
        return <InfluencerVisitsPage user={user} />;
      case 'monthlypnl':
        return <FinancialPage restaurantId={restaurant?.id} onLocationChange={handleFinancialLocation} />;
      case 'audits':
        return <AuditsPage user={user} allRestaurants={allRestaurants} restaurant={restaurant} />;
      case 'cashouts':
        return <CashOutsPage restaurant={restaurant} user={user} />;
      default:
        return <DashboardPage user={user} restaurant={restaurant} onNavigate={handleNavigate} />;
    }
  }

  return (
    <>
      {updateBanner}
      {signOutModal}
      <CommandPalette pages={getPalettePages(user.role)} onNavigate={handleNavigate} />

      <a
        href="#main-content"
        style={{ position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden', zIndex: 99999 }}
        onFocus={e => { e.target.style.cssText = 'position:fixed;left:16px;top:16px;width:auto;height:auto;padding:8px 16px;background:var(--bg);border:2px solid #f59e0b;border-radius:4px;z-index:99999;color:var(--text);font-weight:700;'; }}
        onBlur={e => { e.target.style.cssText = 'position:absolute;left:-9999px;top:auto;width:1px;height:1px;overflow:hidden;z-index:99999;'; }}
      >
        Skip to main content
      </a>
      <div style={updateAvailable ? { paddingTop: '44px' } : {}}>
        <AppLayout
          user={user}
          restaurant={restaurant}
          onNavigate={handleNavigate}
          onLogout={handleLogout}
          onSwitchRestaurant={handleSwitchRestaurant}
          allRestaurants={isPrivileged ? allRestaurants : []}
          onSelectRestaurantTab={handleSelectRestaurantTab}
          theme={theme}
          onToggleTheme={toggleTheme}
          title={(!restaurant && isPrivileged && !ADMIN_GLOBAL_PAGES.includes(page)) ? 'Overview' : PAGE_TITLES[page]}
          subtitle={restaurant?.name || (isPrivileged ? 'All Locations' : '')}
          onSwitchToEmployee={(user.role === 'manager' || (isPrivileged && restaurant)) ? () => { prevPathRef.current = location.pathname; setViewMode('employee'); } : undefined}
          onAdminNotifNavigate={handleAdminNotifNavigate}
        >
          {pushBanner}
          <Suspense fallback={<PageLoader />}>
            {renderPage()}
          </Suspense>
        </AppLayout>
      </div>
    </>
  );
}

function SessionRoutes() {
  const auth = useAuth();
  const toast = useToast();
  const location = useLocation();
  const [params] = useSearchParams();
  const setup = (params.get('setup') && params.get('email')) || (params.get('empsetup') && params.get('restaurant'));
  const content = location.pathname === '/legal-records' && auth.user
    ? <LegalRecordsPage user={auth.user} /> : <AppRoutes auth={auth} />;
  async function signOutPendingReview() {
    try { await disconnectPush(); } catch {
      try { const reg = await navigator.serviceWorker?.getRegistration(); const sub = await reg?.pushManager.getSubscription(); await sub?.unsubscribe(); } catch {}
    }
    try { await auth.logout(); } catch (error) { toast(error.message, 'error'); }
  }
  if (auth.loading) return <PageLoader />;
  if (auth.error && !auth.user) return <main className="legal-shell"><div className="legal-card"><h1>Checking your sign-in</h1><p role="alert">{auth.error}</p><button className="btn btn-primary" onClick={auth.refreshSession}>Try again</button></div></main>;
  if (auth.user && (auth.user.mfaRequired || location.pathname === '/security')) return <SecurityPage key={`${auth.user.id}-${auth.user.mfaRequired}`} user={auth.user} required={auth.user.mfaRequired} onComplete={auth.refreshSession} onLogout={signOutPendingReview} />;
  return auth.user && !setup
    ? <LegalGate key={auth.user.id} user={auth.user} onLogout={signOutPendingReview}>{content}</LegalGate>
    : content;
}

export default function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ToastProvider>
          <ProgressBar />
          <NetworkStatusBanner />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/recover-mfa" element={<MfaRecoveryPage />} />
              <Route path="/admin-invite" element={<AdminInvitePage />} />
              <Route path="/legal" element={<LegalPage />} />
              <Route path="/legal/:documentKey/:version" element={<LegalPage />} />
              <Route path="/kiosk/:token" element={
                <ErrorBoundary fallback={<KioskErrorFallback />}>
                  <KioskLoader />
                </ErrorBoundary>
              } />
              <Route path="/pnl-report/:token" element={
                <Suspense fallback={<PageLoader />}><PnLReportPage /></Suspense>
              } />
              <Route path="/checklist-report/:token" element={
                <Suspense fallback={<PageLoader />}><ChecklistReportPage /></Suspense>
              } />
              <Route path="*" element={<SessionRoutes />} />
            </Routes>
          </Suspense>
        </ToastProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
